<template>
  <div class="thermal-panel">
    <div class="thermal-panel-header">Thermal Controls</div>
    <div class="row">
      <span class="label">Palette:</span>
      <a-select v-model:value="paletteStyle" style="width: 140px" :options="paletteOptions" @change="onPaletteChange" />
    </div>
    <div class="row">
      <span class="label">Gain:</span>
      <a-select v-model:value="gainMode" style="width: 140px" :options="gainOptions" @change="onGainChange" />
    </div>
    <div class="row">
      <span class="label">Isotherm:</span>
      <a-switch v-model:checked="isothermEnabled" @change="onIsothermStateChange" />
      <a-input-number v-model:value="isothermUpper" :min="-40" :max="550" placeholder="upper" style="width: 70px; margin-left: 6px" @change="onIsothermUpperChange" />
      <a-input-number v-model:value="isothermLower" :min="-40" :max="550" placeholder="lower" style="width: 70px; margin-left: 6px" @change="onIsothermLowerChange" />
    </div>
    <div class="row">
      <span class="label">Metering:</span>
      <a-select v-model:value="meteringMode" style="width: 100px" :options="meteringOptions" @change="onMeteringModeChange" />
    </div>
    <div class="row" v-if="meteringMode === IrMeteringMode.SPOT">
      <span class="label">Spot (x, y):</span>
      <a-input-number v-model:value="meteringX" :min="0" :max="1" :step="0.05" style="width: 70px" />
      <a-input-number v-model:value="meteringY" :min="0" :max="1" :step="0.05" style="width: 70px; margin-left: 6px" />
      <Button size="small" ghost @click="onMeteringPointConfirm">Set</Button>
    </div>
    <div class="row" v-if="meteringMode === IrMeteringMode.AREA">
      <span class="label">Area (x,y,w,h):</span>
      <a-input-number v-model:value="meteringX" :min="0" :max="1" :step="0.05" style="width: 60px" />
      <a-input-number v-model:value="meteringY" :min="0" :max="1" :step="0.05" style="width: 60px; margin-left: 4px" />
      <a-input-number v-model:value="meteringWidth" :min="0" :max="1" :step="0.05" style="width: 60px; margin-left: 4px" />
      <a-input-number v-model:value="meteringHeight" :min="0" :max="1" :step="0.05" style="width: 60px; margin-left: 4px" />
      <Button size="small" ghost @click="onMeteringAreaConfirm">Set</Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { defineProps, ref } from 'vue'
import { Button } from 'ant-design-vue'
import {
  useThermalControl,
  IrMeteringMode,
  ThermalPaletteStyle,
  ThermalGainMode,
  SwitchAction,
} from './use-thermal-control'

const props = defineProps<{
  sn: string,
  payloadIndex: string,
}>()

const {
  setIrMeteringMode,
  setIrMeteringPoint,
  setIrMeteringArea,
  setThermalPaletteStyle,
  setThermalGainMode,
  setThermalIsothermState,
  setThermalIsothermUpperLimit,
  setThermalIsothermLowerLimit,
} = useThermalControl()

const paletteOptions = Object.entries(ThermalPaletteStyle)
  .filter(([key]) => Number.isNaN(Number(key)))
  .map(([key, value]) => ({ label: key, value }))

const gainOptions = Object.entries(ThermalGainMode)
  .filter(([key]) => Number.isNaN(Number(key)))
  .map(([key, value]) => ({ label: key, value }))

const meteringOptions = [
  { label: 'Disable', value: IrMeteringMode.DISABLE },
  { label: 'Spot', value: IrMeteringMode.SPOT },
  { label: 'Area', value: IrMeteringMode.AREA },
]

const paletteStyle = ref<ThermalPaletteStyle>(ThermalPaletteStyle.WHITE_HOT)
const gainMode = ref<ThermalGainMode>(ThermalGainMode.AUTOMATIC)
const isothermEnabled = ref(false)
const isothermUpper = ref<number | null>(null)
const isothermLower = ref<number | null>(null)
const meteringMode = ref<IrMeteringMode>(IrMeteringMode.DISABLE)
const meteringX = ref<number | null>(0.5)
const meteringY = ref<number | null>(0.5)
const meteringWidth = ref<number | null>(0.2)
const meteringHeight = ref<number | null>(0.2)

function onPaletteChange (value: ThermalPaletteStyle) {
  setThermalPaletteStyle(props.sn, props.payloadIndex, value)
}

function onGainChange (value: ThermalGainMode) {
  setThermalGainMode(props.sn, props.payloadIndex, value)
}

function onIsothermStateChange (checked: boolean) {
  setThermalIsothermState(props.sn, props.payloadIndex, checked ? SwitchAction.ENABLE : SwitchAction.DISABLE)
}

function onIsothermUpperChange (value: number) {
  if (value !== null && value !== undefined) {
    setThermalIsothermUpperLimit(props.sn, props.payloadIndex, value)
  }
}

function onIsothermLowerChange (value: number) {
  if (value !== null && value !== undefined) {
    setThermalIsothermLowerLimit(props.sn, props.payloadIndex, value)
  }
}

function onMeteringModeChange (value: IrMeteringMode) {
  setIrMeteringMode(props.sn, { payload_index: props.payloadIndex, mode: value })
}

function onMeteringPointConfirm () {
  if (meteringX.value === null || meteringY.value === null) {
    return
  }
  setIrMeteringPoint(props.sn, { payload_index: props.payloadIndex, x: meteringX.value, y: meteringY.value })
}

function onMeteringAreaConfirm () {
  if (meteringX.value === null || meteringY.value === null || meteringWidth.value === null || meteringHeight.value === null) {
    return
  }
  setIrMeteringArea(props.sn, {
    payload_index: props.payloadIndex,
    x: meteringX.value,
    y: meteringY.value,
    width: meteringWidth.value,
    height: meteringHeight.value,
  })
}
</script>

<style lang="scss" scoped>
.thermal-panel {
  width: 100%;
  padding: 5px;
  border: 0.5px solid rgba(255, 255, 255, 0.3);

  .thermal-panel-header {
    font-size: 13px;
    font-weight: 600;
    padding: 2px 0 6px;
  }

  .row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    padding: 2px;
    margin-bottom: 6px;

    .label {
      font-size: 12px;
      color: #aaa;
      margin-right: 6px;
      white-space: nowrap;
    }
  }
}
</style>
